import { useCallback, useEffect, useRef, useState } from 'react';
import { clockOffsetMs, remainingSecondsAt } from './examClock';

/**
 * A countdown a student cannot influence.
 *
 * The offset between this device and the server is measured when the paper
 * opens, and the time remaining is worked out from the deadline on every
 * render rather than being held in state. That matters: holding it would leave
 * a moment, between the paper arriving and the first tick, where the clock
 * still read zero and the paper looked as though its time was up.
 *
 * Working it out from the deadline each time also means a tab a phone suspended
 * comes back showing the truth rather than the minutes it slept through.
 *
 * `resync` lets any later response correct the offset, since every one of them
 * carries the server's time.
 */
export const useExamClock = (deadlineAt?: string, serverNow?: string) => {
  const offsetRef = useRef(0);
  const [, setTick] = useState(0);

  const refresh = useCallback(() => setTick((tick) => tick + 1), []);

  const resync = useCallback(
    (latestServerNow?: string) => {
      if (latestServerNow) {
        offsetRef.current = clockOffsetMs(latestServerNow);
      }
      refresh();
    },
    [refresh],
  );

  // The first sync, from the response that opened the paper.
  useEffect(() => {
    resync(serverNow);
  }, [resync, serverNow]);

  useEffect(() => {
    if (!deadlineAt) {
      return undefined;
    }

    const timer = window.setInterval(refresh, 1000);

    // Coming back to a backgrounded tab should show the right time at once
    // rather than up to a second later.
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [deadlineAt, refresh]);

  // A deadline that cannot be read is treated like no deadline at all. Reading
  // it as zero would submit the paper the moment it opened.
  const hasDeadline = !!deadlineAt && Number.isFinite(new Date(deadlineAt).getTime());
  const remainingSeconds = hasDeadline ? remainingSecondsAt(deadlineAt, offsetRef.current) : 0;

  return {
    remainingSeconds,
    // A paper with no deadline yet has not run out of time; it has not started.
    expired: hasDeadline && remainingSeconds <= 0,
    resync,
  };
};

export default useExamClock;
